import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCases } from '@services/api/caseService';
import { CASE_STATUS } from '@utils/constants';

/**
 * CasesList — Tabbed case list matching order history layout
 *
 * Tabs: New | In progress | Closed
 * Each row: case number + subject + created date + creator + last updated + chevron
 * Status badges: blue (New), amber (In progress), grey (Closed)
 * Filters: date range (from/to)
 * Sort: newest first (default) / oldest first
 * "+ New case" link → /contact
 */

const TAB_CONFIG = [
  { key: CASE_STATUS.NEW, label: 'New', badgeClass: 'bg-electric-blue text-white' },
  { key: CASE_STATUS.IN_PROGRESS, label: 'In progress', badgeClass: 'bg-warning text-carbon-black' },
  { key: CASE_STATUS.CLOSED, label: 'Closed', badgeClass: 'bg-n3 text-n6' },
];

function StatusBadge({ status }) {
  const config = TAB_CONFIG.find(t => t.key === status) || TAB_CONFIG[0];
  return (
    <span className={`inline-block px-2.5 py-0.5 text-xs ${config.badgeClass}`}>
      {config.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function CasesList() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(CASE_STATUS.NEW);
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getCases().then(data => {
      if (mounted) {
        setCases(data);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    let result = cases.filter(c => c.status === activeTab);

    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter(c => new Date(c.createdDate) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(c => new Date(c.createdDate) <= to);
    }

    result.sort((a, b) => {
      const da = new Date(a.createdDate);
      const db = new Date(b.createdDate);
      return sortOrder === 'newest' ? db - da : da - db;
    });

    return result;
  }, [cases, activeTab, sortOrder, dateFrom, dateTo]);

  // Count per tab
  const counts = useMemo(() => {
    const map = {};
    TAB_CONFIG.forEach(t => { map[t.key] = 0; });
    cases.forEach(c => { if (map[c.status] !== undefined) map[c.status]++; });
    return map;
  }, [cases]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header row: title + new case link */}
      <div className="flex items-center justify-between">
        <h2 className="sr-only">Cases</h2>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-electric-blue hover:underline"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New case
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-n2">
        <nav className="flex gap-6" role="tablist">
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                relative pb-3 text-sm font-medium transition-colors
                ${activeTab === tab.key
                  ? 'text-electric-blue'
                  : 'text-n5 hover:text-carbon-black'}
              `}
            >
              {tab.label}
              {counts[tab.key] > 0 && (
                <span className="ml-1.5 text-xs text-n5">({counts[tab.key]})</span>
              )}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue rounded-t" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-n5">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-n5">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
          />
        </div>
        <select
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
          className="ml-auto border border-n3 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-electric-blue"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      {/* Case rows */}
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-n5">
          No {TAB_CONFIG.find(t => t.key === activeTab)?.label.toLowerCase()} cases found.
        </div>
      ) : (
        <ul className="divide-y divide-n2">
          {filtered.map(c => (
            <li key={c.caseId}>
              <Link
                to={`/cases/${c.caseId}`}
                className="flex items-center gap-4 py-4 px-2 hover:bg-n1 rounded transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-n5">{c.caseNumber}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-sm text-carbon-black truncate">
                    {c.subject}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-n5">
                    <span>Created {formatDate(c.createdDate)}</span>
                    {c.createdBy && <span>by {c.createdBy}</span>}
                    <span>Updated {formatDate(c.lastUpdated)}</span>
                  </div>
                </div>
                {/* Chevron */}
                <svg
                  width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="text-n4 group-hover:text-electric-blue transition-colors flex-shrink-0"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
